import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";
import type { ChallengeType } from "@expert/shared-types/src/sales";
import type { KyInstance } from "ky";

export interface ChallengeCondition {
    goalType: string;
    targetValue: number;
    currentValue: number;
    threshold: "TARGET" | "UPPER_LIMIT" | "LOWER_LIMIT";
}

export interface ChallengeDetails {
    teamName?: string;
    empIds: string[];
    conditions: ChallengeCondition[];
    wasSuccessful?: boolean | null;
    finishDate?: string | null;
}

export interface ChallengeInfo {
    challengeId: number;
    challengeName: string;
    challengeType: ChallengeType;
    startDate: string;
    deadline: string;
    announcement: {
        challengeParticipantId: number;
        ackStart: boolean;
        ackEnd: boolean;
    };
    questionList?: string[];
    badgeType: string;
    earnedBadges?: {
        badgeType: string;
    }[];
    details: ChallengeDetails[];
    timestamp: string;
}

export interface QuizChallenge extends ChallengeInfo {
    answeredQuestions?: AnsweredQuestion[];
    metadata: { questionList: { questionId: string; topic: string }[] };
}

export interface AnsweredQuestion {
    id: number;
    questionId: string;
    topic: string;
    isCorrectAnswer: boolean;
}

export const getChallengesForEmployee = async (
    client: KyInstance,
    logger: Logger,
    partner: Partner,
): Promise<ChallengeInfo[] | null> => {
    const childLogger = logger.child({ module: "getChallengesForEmployee" });
    try {
        const response = await client.get(`v1/participant/challenges?partner=${partner}`);
        return response.status === 204 ? null : await response.json<ChallengeInfo[]>();
    } catch (error: unknown) {
        childLogger.warn({ error }, "Failed to fetch challenges for this participant");
        throw error;
    }
};
